import { useState, useEffect, useCallback, useContext } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { Context } from '../../AuthContext';
import WorkbenchType from './components/WorkbenchType';
import Tabs, { TAB_KEYS } from './components/Tabs';
import PersonalMainLayout from './layouts/PersonalMainLayout';
import VerticalMainLayout from './layouts/VerticalMainLayout';
import Tasks from './components/Tasks';
import Notes from './components/Notes';
import Slack from './components/Slack';
import Links from './components/Links';
import NotificationSettings from './components/NotificationSettings';
import ComingSoon from './components/ComingSoon';
import Files from './components/Files';
import Calendar from './components/Calendar';
import VerticalCalendar from './components/VerticalCalendar';
import WatchGuideLink from '../../components/WatchGuideLink/WatchGuideLink';

// Video guide shown below the tab bar, keyed by workbench type and active tab.
// The disabled "Additional Tools" tab has no guide.
const WORKBENCH_GUIDES = {
  personal: {
    [TAB_KEYS.MAIN]: {
      videoId: 'personal-dashboard-overview',
      label: 'Watch Guide: Understanding Your Personal Workbench',
    },
    [TAB_KEYS.FILES]: { videoId: 'personal-files', label: 'Watch Guide: Personal Files' },
    [TAB_KEYS.CALENDAR]: { videoId: 'personal-calendar', label: 'Watch Guide: Personal Calendar' },
    [TAB_KEYS.TASKS]: { videoId: 'personal-tasks', label: 'Watch Guide: Personal Tasks' },
    [TAB_KEYS.NOTEBOOK]: { videoId: 'personal-notebook', label: 'Watch Guide: Personal Notebook' },
    [TAB_KEYS.LINKS]: { videoId: 'personal-links', label: 'Watch Guide: Personal Links' },
  },
  vertical: {
    [TAB_KEYS.MAIN]: {
      videoId: 'vertical-dashboard-overview',
      label: 'Watch Guide: Understanding The Vertical Workbench',
    },
    [TAB_KEYS.FILES]: { videoId: 'vertical-files', label: 'Watch Guide: Vertical Files' },
    [TAB_KEYS.CALENDAR]: { videoId: 'vertical-calendar', label: 'Watch Guide: Vertical Calendar' },
    [TAB_KEYS.TASKS]: { videoId: 'vertical-tasks', label: 'Watch Guide: Vertical Tasks' },
    [TAB_KEYS.NOTEBOOK]: { videoId: 'vertical-notebook', label: 'Watch Guide: Vertical Notebook' },
    [TAB_KEYS.LINKS]: { videoId: 'vertical-links', label: 'Watch Guide: Vertical Links' },
  },
};

const DEEP_LINK_TABS = { slack: TAB_KEYS.SLACK, tasks: TAB_KEYS.TASKS };

const Workbench = () => {
  const [searchParams] = useSearchParams();
  // Allow deep-linking to a specific tab (e.g. the sidebar "Vertical Slack" button → ?tab=slack,
  // or the Assigned Tasks pop-up → ?tab=tasks).
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(DEEP_LINK_TABS[tabParam] ?? TAB_KEYS.MAIN);
  // The personal and vertical workbench routes share this component instance, so a
  // new ?tab= must also be applied after mount.
  useEffect(() => {
    if (DEEP_LINK_TABS[tabParam] !== undefined) setActiveTab(DEEP_LINK_TABS[tabParam]);
  }, [tabParam]);
  const location = useLocation();
  const { vertical_id: verticalId } = useParams();
  const { user } = useContext(Context);
  const isVertical = location.pathname.endsWith('/workbench/vertical');
  const guide = WORKBENCH_GUIDES[isVertical ? 'vertical' : 'personal'][activeTab];

  // Slack and Additional Tools only exist on the Vertical Workbench; fall back to Main
  // when switching to the Personal Workbench (this component instance is reused).
  useEffect(() => {
    if (!isVertical && (activeTab === TAB_KEYS.SLACK || activeTab === TAB_KEYS.ADDITIONAL_TOOLS)) {
      setActiveTab(TAB_KEYS.MAIN);
    }
  }, [isVertical, activeTab]);

  // Slack connection/activity is held here (not in the Slack tab) because the
  // new-activity dot must render on the tab bar even when another tab is open.
  const [slack, setSlack] = useState({
    loading: true,
    connected: false,
    workspace: null,
    hasNewActivity: false,
  });

  const fetchSlackStatus = useCallback(async () => {
    if (!isVertical || !verticalId || !user?.uid) {
      setSlack({ loading: false, connected: false, workspace: null, hasNewActivity: false });
      return;
    }
    try {
      const token = await user.getIdToken();
      const res = await fetch(
        `${process.env.REACT_APP_FLASK_WEBSERVER}oauth/slack/status?vertical_id=${encodeURIComponent(
          verticalId
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        setSlack({ loading: false, connected: false, workspace: null, hasNewActivity: false });
        return;
      }
      const data = await res.json();
      setSlack({
        loading: false,
        connected: !!data.connected,
        workspace: data.connected
          ? { name: data.team_name, url: data.url, teamId: data.team_id }
          : null,
        hasNewActivity: !!data.has_new_activity,
      });
    } catch (e) {
      setSlack({ loading: false, connected: false, workspace: null, hasNewActivity: false });
    }
  }, [isVertical, verticalId, user]);

  useEffect(() => {
    fetchSlackStatus();
    if (!isVertical) return undefined;
    // Light poll so the new-activity dot surfaces without a manual page refresh.
    const id = setInterval(fetchSlackStatus, 60000);
    return () => clearInterval(id);
  }, [fetchSlackStatus, isVertical]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 80px)',
        width: 'calc(100vw - 400px)',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <WorkbenchType />
          <Tabs
            value={activeTab}
            onChange={setActiveTab}
            slackHasActivity={slack.connected && slack.hasNewActivity}
          />
        </div>
        {(activeTab === TAB_KEYS.TASKS || activeTab === TAB_KEYS.NOTEBOOK) && (
          <ComingSoon trello={activeTab === TAB_KEYS.TASKS && true} />
        )}
      </div>

      {guide && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 1 }}>
          <WatchGuideLink videoId={guide.videoId} label={guide.label} />
        </Box>
      )}

      {activeTab === TAB_KEYS.MAIN &&
        (isVertical ? (
          <VerticalMainLayout onNavigateTab={setActiveTab} />
        ) : (
          <PersonalMainLayout onNavigateTab={setActiveTab} />
        ))}
      {activeTab === TAB_KEYS.FILES && <Files isVertical={isVertical} verticalId={verticalId} />}
      {activeTab === TAB_KEYS.CALENDAR &&
        (isVertical ? <VerticalCalendar verticalId={verticalId} /> : <Calendar />)}
      {activeTab === TAB_KEYS.TASKS && <Tasks isVertical={isVertical} verticalId={verticalId} />}
      {activeTab === TAB_KEYS.NOTEBOOK && <Notes isVertical={isVertical} verticalId={verticalId} />}
      {activeTab === TAB_KEYS.SLACK && (
        <Slack verticalId={verticalId} slack={slack} onRefresh={fetchSlackStatus} />
      )}
      {activeTab === TAB_KEYS.LINKS && <Links isVertical={isVertical} verticalId={verticalId} />}
      {activeTab === TAB_KEYS.ADDITIONAL_TOOLS && isVertical && (
        <NotificationSettings key={verticalId} verticalId={verticalId} />
      )}
    </Box>
  );
};

export default Workbench;
